<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <div class="page-header">
          <BreadCrumb />
        </div>
      </div>
    </div>
    <b-card>
      <div class="row d-flex mb-4 mb-xl-2 justify-content-between">
        <h4 class="col-12 col-xl-2 mb-2 font-weight-bold">標籤管理</h4>
        <div
          class="
            col-12 col-xl-6
            d-flex
            flex-column flex-xl-row
            align-items-start align-items-xl-center
          "
        >
          <b-button
            v-if="isCanSyncTag"
            @click="syncFromCresclab"
            :disabled="isSyncing"
            class="float-right btn-inverse-success refresh-btn mb-2 mb-xl-0 mr-3"
            >
            <i class="fa fa-refresh fa-lg"></i>
            自漸強同步標籤
          </b-button>
          <b-button
            v-if="checkPermission([consts.TAG_LIST_CREATE])"
            class="flex-shrink-0 mb-2 mb-xl-0 mr-3"
            variant="primary"
            :to="{ name: 'TagCreate' }"
            ><i class="fa fa-plus"></i>新增標籤</b-button
          >
          <b-input-group class="mb-2">
            <b-form-input v-model="query.keyword" placeholder="搜尋" @keyup.enter="handlePageReset"></b-form-input>
            <b-input-group-append>
              <b-button @click="handlePageReset"><i class="fa fa-search"></i></b-button>
            </b-input-group-append>
          </b-input-group>
        </div>
      </div>
      <b-card-text>
        <div class="row">
          <div class="col-12">
            <b-overlay :show="showLoading" rounded="sm">
              <b-table
                striped
                hover
                responsive
                :items="keywords"
                :fields="fields"
              >
                <template #cell(actions)="data">
                  <b-button
                    variant="inverse-info"
                    :to="{
                      name: 'TagCustomerList',
                      params: { tag_id: data.item.id },
                    }"
                  >
                  <span class="mdi mdi-account-multiple"></span>會員列表
                 </b-button>
                  <b-button
                    variant="inverse-primary"
                    :to="{
                      name: 'TagView',
                      params: { tag_id: data.item.id },
                    }"
                  >
                    <span class="mdi mdi-eye mr-1"></span>查看
                  </b-button>
                  <b-button
                    variant="inverse-warning"
                    :to="{
                      name: 'TagEdit',
                      params: { tag_id: data.item.id },
                    }"
                  >
                    <span class="mdi mdi-lead-pencil mr-1"></span>編輯
                  </b-button>
                  <b-button
                    variant="inverse-danger"
                    @click="deleteTags(data.item)"
                  >
                    <span class="mdi mdi-delete mr-1"></span>刪除
                  </b-button>
                </template>
              </b-table>
            </b-overlay>
          </div>
        </div>
        <CustomPagination
          :currentPage="query.page"
          :totalRows="totalRows"
          :perPage="query.per_page"
          @page-change="handlePageChange"
          @per-page-change="handlePerPageChange"
        />
      </b-card-text>
    </b-card>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import tagApi from "@/apis/tag";
import PermissionChecker from "@/utils/PermissionChecker";
import * as consts from "@/consts";
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue";
import { paginationMixin } from "@/mixins/pagination";
import { updateQueryFromRoute, updateUrl } from "@/utils/updateUrl";
import BreadCrumb from "@/components/Dashboard/BreadCrumb";

export default {
  components: { CustomPagination, BreadCrumb },
  mixins: [ paginationMixin ],
  data() {
    return {
      consts: consts,
      totalRows: 0,
      showLoading: true,
      keywords: [],
      keyword: null,
      isSyncing: false,
      fields: [
        {
          key: "name",
          label: "標籤",
        },
        {
          key: "outer_code",
          label: "外部代號",
        },
        {
          key: "customers_count",
          label: "會員",
        },
        {
          key: "actions",
          label: "管理",
        },
      ],
      initialized: false,
      query: {
        page: 1,
        per_page: 10,
        keyword: "",
      }
    };
  },
  computed: {
    ...mapState("general", {
      organization: (state) => state.organization,
    }),
    ...mapGetters({
      getModuleConfig: "dashboardModule/getConfig",
    }),
    queryWatcher() {
      return `${this.query.page}-${this.query.per_page}`;
    },
    isCanSyncTag() {
      return this.getModuleConfig("cresclab", "tag.instant_to_cresclab_tag");
    },
  },
  watch: {
    queryWatcher: {
      handler() {
        if (!this.initialized) return;
        updateUrl(this.query, this.$store, this.$router);
      },
    },
    $route(to) {
      if (!this.initialized) return;
      this.query = { ...to.query };
      this.getTags();
    },
  },
  created() {
    this.query = updateQueryFromRoute(this.$route, this.$store, this.query);
  },
  mounted() {
    this.init();
  },
  methods: {
    async init() {
      await this.getTags();
      this.initialized = true;
    },
    async syncFromCresclab() {
        this.isSyncing = true;
        this.showLoading = true;
        await tagApi.syncFromCresclabTag(this.organization);
        await this.getTags();
        this.$swal.fire({
          title: "成功",
          type: "success",
          text: "標籤已同步",
        });
        this.showLoading = false;
        this.isSyncing  = false;
    },
    async getTags() {
      this.showLoading = true;
      try {
        let params = { ...this.query };

        const { data } = await tagApi.getTags(this.organization, params);
        this.keywords = data.data;
        this.totalRows = data.meta.total;
      } catch (error) {
        console.error(error);
        if (error.status !== 401) {
          this.$swal.fire({
            title: "錯誤",
            type: "error",
            text: "取得關鍵字資料錯誤",
          });
        }
      }
      this.showLoading = false;
    },
    async deleteTags(tag) {
      await this.$swal
        .fire({
          title: "你確定要刪除嗎？",
          html: `
            <div class="d-block">
              <div class="my-3">
                <div>關鍵字：${tag.name}</div>
              </div>
              <div>刪除後，相關資料將無法回復，<br/>請您確認！</div>
            </div>`,
          type: "warning",
          confirmButtonText: "確定刪除",
          cancelButtonText: "不刪除",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          reverseButtons: true,
        })
        .then(async (result) => {
          if (result.value) {
            let response = await tagApi.removeTag(this.organization, tag);
            if (response.status === 200) {
              this.$swal.fire({
                title: "成功",
                type: "success",
                text: "成功刪除 " + tag.name,
              });
              this.getTags();
            } else {
              if (response.data.message) {
                this.$swal.fire({
                  title: "錯誤",
                  type: "error",
                  text: response.data.message,
                });
              }
            }
          }
        });
    },
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    },
  },
};
</script>

<style scoped>
.btn + .btn {
  margin-left: 0.5rem;
}

.vdp-datepicker {
  min-width: 15rem;
}

.refresh-btn {
    min-width: 160px;
}
</style>
